import { noOpDispatcher } from "./noOpDispatcher";
import type {
  PulseEventDispatcher,
  EventDispatcherOptions,
  Environment,
  Logger,
  GetHoustonOptimizelySdkOptions,
} from "./typings";
import { errorToString, getUrlTemplate } from "./utils";
import { HOUSTON_URL_TEMPLATE } from "./constants/config";

interface FactoryConfig {
  pulseEventDispatcher: PulseEventDispatcher;
}

export const getHoustonOptimizelySdkOptionsFactory = ({
  pulseEventDispatcher,
}: FactoryConfig): GetHoustonOptimizelySdkOptions => {
  return (sdkOptions, options) => {
    try {
      const sdkKey = sdkOptions.tenant;
      const urlTemplate = getUrlTemplate(
        sdkOptions.environment,
        sdkOptions.eventDispatcherOptions?.urlTemplates ?? HOUSTON_URL_TEMPLATE,
      );
      const eventDispatcher = createEventDispatcher({
        pulseEventDispatcher,
        providerId: sdkOptions.providerId,
        deployStage: sdkOptions.environment,
        options: sdkOptions.eventDispatcherOptions,
        logger: sdkOptions.logger,
      });

      return {
        sdkKey,
        eventDispatcher,
        datafileOptions: {
          urlTemplate,
        },
      };
    } catch (error) {
      sdkOptions.logger?.({
        type: "error",
        message: `[getHoustonOptimizelySdkOptions]: ${errorToString(error)}`,
      });

      options?.onError?.(
        error instanceof Error ? error : new Error(String(error)),
      );

      return {
        eventDispatcher: noOpDispatcher,
      };
    }
  };
};

const createEventDispatcher = ({
  pulseEventDispatcher,
  providerId,
  deployStage,
  options,
  logger,
}: {
  pulseEventDispatcher: PulseEventDispatcher;
  providerId: string;
  deployStage: Environment;
  options?: Partial<EventDispatcherOptions>;
  logger?: Logger;
}) => {
  const eventDispatcher = pulseEventDispatcher(providerId, {
    deployStage,
    logger,
    ...options,
  });

  if (!providerId || !eventDispatcher) {
    throw new Error(
      `Could not create event dispatcher with providerId: '${providerId}'`,
    );
  }

  return eventDispatcher;
};
